import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';

const Navigation = ({
  links = [],
  contentLink = null,
  level = 1,
  currentMenu = null,
  onActivate = null,
}) => {
  const menuRef = useRef(null);
  const menuTriggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const isTopLevel = level === 1;

  const [rootActiveMenu, setRootActiveMenu] = React.useState(null);

  const activeMenu = isTopLevel ? rootActiveMenu : currentMenu;

  const additionClass =
    contentLink && contentLink.attributes
      ? contentLink.attributes.cssValue
      : null;
  const selectedClass =
    contentLink && contentLink.isSelected ? 'navbar__link--selected' : '';
  const menuId = useMemo(
    () =>
      `navbar-menu-${level}-${
        contentLink && contentLink.url ? contentLink.url : 'root'
      }`,
    [level, contentLink && contentLink.url]
  );

  const openMenu = useCallback(() => {
    if (!menuRef.current) return;
    menuRef.current.classList.add('navbar__menu--show');
    if (menuTriggerRef.current) {
      menuTriggerRef.current.classList.add('menu--open');
    }
    if (isTopLevel) {
      document.body.classList.add('menu--open');
    } else {
      document.body.classList.add(`menu--lvl${level}`);
    }
    setIsOpen(true);
  }, [isTopLevel, level]);

  const closeMenu = useCallback(() => {
    if (!menuRef.current) return;
    menuRef.current.classList.remove('navbar__menu--show');
    if (menuTriggerRef.current) {
      menuTriggerRef.current.classList.remove('menu--open');
    }
    if (isTopLevel) {
      document.body.classList.remove('menu--open');
      setRootActiveMenu(null);
    } else {
      document.body.classList.remove(`menu--lvl${level}`);
    }
    setIsOpen(false);
  }, [isTopLevel, level]);

  const toggleMenu = useCallback(() => {
    if (!menuRef.current) return;
    if (menuRef.current.classList.contains('navbar__menu--show')) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [closeMenu, openMenu]);

  const handleMouseEnter = (menuName) => {
    if (isTopLevel) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setRootActiveMenu(menuName);
    }
  };

  const handleMouseLeave = () => {
    if (!isTopLevel) return;
    timeoutRef.current = setTimeout(() => {
      setRootActiveMenu(null);
    }, 150); // Small delay to prevent menu from closing when moving to submenu
  };

  const onTriggerKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      } else if (e.key === 'Escape') {
        closeMenu();
      }
    },
    [toggleMenu, closeMenu]
  );

  useEffect(() => {
    const onDocKey = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', onDocKey);
    }
    return () => document.removeEventListener('keydown', onDocKey);
  }, [isOpen, closeMenu]);

  return (
    <Fragment>
      {isTopLevel && (
        <button
          className="flex lg:hidden"
          ref={menuTriggerRef}
          onClick={toggleMenu}
          onKeyDown={onTriggerKeyDown}
          type="button"
          aria-expanded={isOpen}
          aria-controls={menuId}
          aria-label="Menu"
          tabIndex={0}
        >
          <span className="icon icon-outlined" aria-hidden="true">
            menu
          </span>
        </button>
      )}
      {contentLink && (
        <div className="flex items-center gap-2">
          <a
            className={`grid grow ${selectedClass} ${additionClass || ''} ${
              level === 2
                ? 'after:bg-accent relative hidden h-10 overflow-hidden px-4 py-2 text-lg leading-6 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:translate-y-1 after:transition-all after:duration-200 after:ease-in-out after:content-[""] hover:font-bold hover:after:translate-y-0 lg:grid lg:text-center lg:text-base'
                : level === 3
                  ? 'text-accent grid-cols-[1fr_auto] items-center gap-1 text-sm font-bold capitalize'
                  : 'text-sm font-normal text-[#333] hover:underline'
            }`}
            href={contentLink.url || '#'}
            onClick={
              links.length > 0 && onActivate
                ? (e) => {
                    if (
                      window.matchMedia('(pointer: coarse)').matches &&
                      currentMenu !== contentLink.name
                    ) {
                      e.preventDefault();
                      onActivate();
                    }
                  }
                : undefined
            }
          >
            <span
              className="invisible col-start-1 row-start-1 truncate whitespace-nowrap font-bold"
              aria-hidden="true"
            >
              {contentLink.name}
            </span>
            <span className="col-start-1 row-start-1 truncate whitespace-nowrap">
              {contentLink.name}
            </span>
            {level === 3 && (
              <span className="icon icon-outlined shrink-0" aria-hidden="true">
                chevron_right
              </span>
            )}
          </a>
          {links.length > 0 && (
            <button
              className={[
                'flex w-full items-center gap-2 px-4 py-5 lg:hidden',
                level === 1 ? 'hidden' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={toggleMenu}
              onKeyDown={onTriggerKeyDown}
              aria-expanded={isOpen}
              aria-controls={menuId}
              aria-label={`Toggle submenu ${contentLink.name}`}
              type="button"
            >
              <span className="text-accent grow truncate text-left text-base font-bold leading-6">
                {contentLink.name}
              </span>
              <span
                className="icon icon-outlined inline-flex shrink-0"
                aria-hidden="true"
              >
                chevron_right
              </span>
            </button>
          )}
        </div>
      )}
      {links.length > 0 && (
        <div
          className={[
            !isOpen && level !== 1 && 'max-lg:hidden',
            level === 2 &&
              `${contentLink && contentLink.name && activeMenu === contentLink.name ? 'block translate-x-0' : '-translate-x-full lg:hidden'} lg:rounded-radius lg:absolute lg:left-0 lg:right-0 lg:top-[calc(100%+0.5rem)] lg:max-h-[calc(100svh-125px)] lg:w-full lg:overflow-y-auto lg:bg-white lg:p-10`,
            level === 1 &&
              `fixed left-0 right-0 top-0 z-10 w-[92vw] bg-white transition-all duration-200 ease-in-out lg:static lg:w-full lg:bg-transparent ${isOpen ? 'translate-x-0' : '-translate-x-full'} overflow-y-auto lg:translate-x-0`,
            level > 1 &&
              activeMenu &&
              activeMenu !== contentLink?.name &&
              'hidden lg:block',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              'flex h-[var(--mobile-header-height)] items-center justify-between gap-4 px-4 py-5 shadow-[inset_0_-1px_0_rgba(0,0,0,0.10)] lg:hidden',
              level !== 1 && 'hidden',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <a href="/" className="inline-block">
              <img
                src="/ui/images/sunwind-logo.svg"
                alt="Sundwind"
                className="h-5 w-auto"
              />
            </a>
            <button
              onClick={closeMenu}
              onKeyDown={onTriggerKeyDown}
              aria-expanded={isOpen}
              aria-controls={menuId}
              aria-label="Close menu"
              type="button"
            >
              <span className="icon icon-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>
          {level === 2 && (
            <button
              onClick={closeMenu}
              onKeyDown={onTriggerKeyDown}
              aria-expanded={isOpen}
              aria-controls={menuId}
              aria-label="Back to main menu"
              type="button"
              className="flex w-full items-center gap-2 bg-[#E8E8E8] px-4 py-5 lg:hidden"
            >
              <span className="icon icon-outlined" aria-hidden="true">
                chevron_left
              </span>
              <span className="text-accent text-base font-bold leading-6">
                {translate('menu.backToMainMenuText')}
              </span>
            </button>
          )}
          <ul
            id={menuId}
            className={[
              isTopLevel &&
                'relative flex flex-col gap-4 lg:flex-row lg:justify-center lg:gap-0',
              // level === 2 &&
              //   `${(contentLink && contentLink.name && activeMenu === contentLink.name) ? 'block' : 'hidden'} absolute bg-white w-full max-h-[calc(100svh-125px)] overflow-y-auto left-0 right-0 top-[calc(100%+0.5rem)] p-10 columns-4 gap-4 rounded-radius`,
              level === 2 && 'lg:columns-4 lg:gap-16',
              level > 2 && 'grid gap-2',
              !isOpen && level !== 1 && 'max-lg:hidden',
            ]
              .filter(Boolean)
              .join(' ')}
            ref={menuRef}
            role="menu"
            data-active-menu={
              contentLink && contentLink.name && activeMenu === contentLink.name
                ? activeMenu
                : undefined
            }
            // style={{columnFill: 'auto'}}
          >
            {!isTopLevel && (
              <li className="navbar__menu-header">
                {!contentLink ? (
                  <span
                    className="navbar__icon navbar__icon--close"
                    onClick={toggleMenu}
                    onKeyDown={onTriggerKeyDown}
                    role="button"
                    tabIndex={0}
                  ></span>
                ) : (
                  <Fragment>
                    <a
                      href={contentLink.url || '/'}
                      className="text-accent flex w-full items-baseline gap-2 px-4 py-6 font-bold lg:hidden"
                    >
                      <span className="grow truncate text-2xl">
                        {contentLink.name}
                      </span>
                      <span className="shrink-0 text-base">See All</span>
                    </a>
                  </Fragment>
                )}
              </li>
            )}
            {links.map((link, i) => (
              <li
                className={[
                  level === 2 ? 'mb-6 break-inside-avoid' : '',
                  'border-accent/10 border-b lg:border-0',
                ]
                  .filter(Boolean)
                  .join(' ')}
                key={
                  link && (link.url || link.name)
                    ? `${link.url || link.name}-${i}`
                    : i
                }
                // onMouseOver={mouseEnter}
                // onMouseOut={mouseLeave}
                onMouseEnter={() => handleMouseEnter(link.name)}
                onMouseLeave={() => handleMouseLeave()}
                role="none"
              >
                <Navigation
                  currentMenu={activeMenu}
                  links={link.links}
                  contentLink={link}
                  level={level + 1}
                  onActivate={
                    isTopLevel ? () => handleMouseEnter(link.name) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      {isTopLevel && (rootActiveMenu !== null || isOpen) && (
        <div
          // className="navbar__backdrop"
          className="fixed inset-0 -z-10 bg-black/50"
          onClick={closeMenu}
          onKeyDown={(e) => e.key === 'Escape' && closeMenu()}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          aria-hidden={!isOpen}
        ></div>
      )}
    </Fragment>
  );
};

export default memo(Navigation);
